import { useEffect, useMemo, useState } from 'react';
import { Image, Linking, Platform, Pressable, ScrollView, Text, TextInput, View, useWindowDimensions } from 'react-native';
import { AlertTriangle, Camera, Clock, Download, FileText, History, MessageCircle, PackageCheck, Pencil, Plus, Trash2 } from 'lucide-react-native';

import { Badge, Button, Card, EmptyState, Sheet, SkeletonRows, TextField } from '@/components/ui';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import {
  jaulasQuery,
  prestamosJaulaQuery,
  upsertJaula,
  prestarJaula,
  devolverJaula,
  eliminarJaula,
  type Jaula,
  type PrestamoJaula,
} from '@/services/firebase/jaulas';
import { subirACloudinary } from '@/services/cloudinary';
import { exportPrestamosJaulaExcel, exportPrestamosJaulaPDF, type FilaPrestamoJaula } from '@/services/export';
import { useAuth, selectIsAdmin, selectIsRecepcion } from '@/stores/authStore';
import { colors, radius, typography } from '@/theme/tokens';
import { formatDateTime, whatsappLink } from '@/utils/format';

// ---- helpers de fecha (las fechas "date" se guardan a medianoche UTC) ----
const fechaISO = (ts: any): string | null => {
  const d = ts?.toDate?.();
  if (!d) return null;
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
};
const hoyISO = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fmtISO = (iso: string | null): string => (iso ? iso.split('-').reverse().join('/') : '—');
const diasRestantes = (hastaISO: string | null): number | null => {
  if (!hastaISO) return null;
  const [y, m, d] = hastaISO.split('-').map(Number);
  const hasta = new Date(y, m - 1, d);
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  return Math.round((hasta.getTime() - hoy.getTime()) / 86400000);
};
const estaVencida = (j: Jaula): boolean => {
  if (j.estado !== 'prestada') return false;
  const h = fechaISO(j.prestadaHasta);
  return !!h && h < hoyISO();
};

function elegirFoto(onPicked: (f: Blob) => void, onError: (m: string) => void) {
  if (Platform.OS !== 'web') {
    onError('Subir foto solo disponible en web por ahora.');
    return;
  }
  // @ts-ignore web-only
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.onchange = () => {
    const file = input.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      onError('Imagen muy pesada (máx 10 MB).');
      return;
    }
    onPicked(file);
  };
  input.click();
}

export function JaulasScreen() {
  const { width } = useWindowDimensions();
  const wide = width >= 760;
  const isAdmin = useAuth(selectIsAdmin);
  const isRecepcion = useAuth(selectIsRecepcion);
  const perfil = useAuth((s) => s.perfil);
  const canManage = isAdmin || isRecepcion;

  const { data: jaulas, loading } = useFirestoreCollection<Jaula>(jaulasQuery);
  const { data: prestamos } = useFirestoreCollection<PrestamoJaula>(prestamosJaulaQuery);

  const [detalle, setDetalle] = useState<Jaula | null>(null);
  const [editar, setEditar] = useState<Jaula | null>(null);
  const [nueva, setNueva] = useState(false);

  const vencidas = useMemo(() => jaulas.filter(estaVencida), [jaulas]);
  const disponibles = jaulas.filter((j) => j.estado !== 'prestada').length;
  const cardW = wide ? 220 : '47%';

  return (
    <ScrollView contentContainerStyle={{ padding: wide ? 28 : 16, gap: 16, paddingBottom: 90 }}>
      {/* Encabezado */}
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <View>
          <Text style={{ fontFamily: typography.display.fontFamily, fontSize: wide ? 24 : 20, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
            Jaulas en préstamo
          </Text>
          <Text style={{ color: colors.ink[500], fontSize: 13, marginTop: 2 }}>
            {disponibles} disponible{disponibles === 1 ? '' : 's'} · {jaulas.length - disponibles} prestada{jaulas.length - disponibles === 1 ? '' : 's'}
            {vencidas.length > 0 ? ` · ${vencidas.length} vencida${vencidas.length === 1 ? '' : 's'}` : ''}
          </Text>
        </View>
        {canManage ? (
          <Button label="Nueva jaula" size="sm" iconLeft={<Plus size={14} color={colors.white} />} onPress={() => setNueva(true)} />
        ) : null}
      </View>

      {/* Aviso de vencidas (banner) */}
      {vencidas.length > 0 ? (
        <Pressable
          onPress={() => {
            setDetalle(vencidas[0]);
          }}
          style={({ hovered }) => ({
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
            padding: 14,
            borderRadius: radius.xl,
            backgroundColor: hovered ? colors.danger[50] : '#FEF2F2',
            borderWidth: 1.5,
            borderColor: colors.danger[500],
            // @ts-ignore web only
            cursor: 'pointer',
          })}
        >
          <AlertTriangle size={20} color={colors.danger[600]} />
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 14, fontWeight: typography.weights.bold, color: colors.danger[600] }}>
              {vencidas.length === 1 ? `${vencidas[0].nombre} está vencida` : `${vencidas.length} jaulas vencidas`}
            </Text>
            <Text style={{ fontSize: 12, color: colors.ink[600] }}>
              Tocá para ver los datos y llamar al cliente.
            </Text>
          </View>
        </Pressable>
      ) : null}

      {/* Grid de jaulas */}
      {loading ? (
        <SkeletonRows rows={3} />
      ) : jaulas.length === 0 ? (
        <Card>
          <EmptyState illustration="shelf" title="Sin jaulas" message={canManage ? 'Agregá tu primera jaula.' : 'Todavía no hay jaulas cargadas.'} />
        </Card>
      ) : (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>
          {jaulas.map((j) => (
            <View key={j.id} style={{ width: cardW as any }}>
              <JaulaCard jaula={j} onPress={() => setDetalle(j)} onEdit={canManage ? () => setEditar(j) : undefined} />
            </View>
          ))}
        </View>
      )}

      {/* Historial de préstamos (debajo de las jaulas) */}
      <HistorialPrestamos prestamos={prestamos} wide={wide} />

      {/* Sheets */}
      <DetalleJaulaSheet
        jaula={detalle}
        canManage={canManage}
        perfilNombre={perfil?.nombre ?? ''}
        onClose={() => setDetalle(null)}
      />
      <EditarJaulaSheet jaula={editar} visible={!!editar || nueva} onClose={() => { setEditar(null); setNueva(false); }} />

    </ScrollView>
  );
}

// ============================================================================
// Tarjeta de jaula (foto con borde verde/rojo + sello OCUPADA en B&N si prestada)
// ============================================================================
function JaulaCard({ jaula, onPress, onEdit }: { jaula: Jaula; onPress: () => void; onEdit?: () => void }) {
  const prestada = jaula.estado === 'prestada';
  const vencida = estaVencida(jaula);
  const borde = prestada ? colors.danger[500] : colors.success[500];

  return (
    <Pressable
      onPress={onPress}
      style={({ hovered }) => ({
        borderRadius: radius['2xl'],
        borderWidth: 3,
        borderColor: borde,
        backgroundColor: colors.white,
        overflow: 'hidden',
        transform: [{ scale: hovered ? 1.01 : 1 }],
        // @ts-ignore web only
        cursor: 'pointer',
      })}
    >
      {/* Foto */}
      <View style={{ height: 150, backgroundColor: colors.crema[100], alignItems: 'center', justifyContent: 'center' }}>
        {jaula.fotoUrl ? (
          <Image
            source={{ uri: jaula.fotoUrl }}
            style={{
              width: '100%',
              height: '100%',
              // @ts-ignore web only (escala de grises cuando está ocupada)
              filter: prestada ? 'grayscale(100%)' : 'none',
            }}
            // @ts-ignore web (foto completa, sin recortar)
            resizeMode="contain"
          />
        ) : (
          <View style={{ alignItems: 'center', gap: 4 }}>
            <Camera size={26} color={colors.ink[300]} />
            <Text style={{ fontSize: 11, color: colors.ink[400] }}>Sin foto</Text>
          </View>
        )}

        {/* Sello OCUPADA */}
        {prestada ? (
          <View
            style={{
              position: 'absolute',
              transform: [{ rotate: '-12deg' }],
              borderWidth: 3,
              borderColor: vencida ? colors.danger[600] : 'rgba(120,30,71,0.85)',
              borderRadius: 8,
              paddingHorizontal: 12,
              paddingVertical: 4,
              backgroundColor: 'rgba(255,255,255,0.18)',
            }}
          >
            <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 22, fontWeight: typography.weights.extrabold, letterSpacing: 2, color: vencida ? colors.danger[600] : 'rgba(120,30,71,0.95)' }}>
              OCUPADA
            </Text>
          </View>
        ) : null}

        {/* Editar (admin/recepción) */}
        {onEdit ? (
          <Pressable
            onPress={(e: any) => {
              e?.stopPropagation?.();
              onEdit();
            }}
            style={{ position: 'absolute', top: 6, right: 6, width: 30, height: 30, borderRadius: 8, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.9)' }}
          >
            <Pencil size={14} color={colors.ink[600]} />
          </Pressable>
        ) : null}
      </View>

      {/* Pie */}
      <View style={{ padding: 12, gap: 4 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <Text style={{ fontSize: 15, fontWeight: typography.weights.bold, color: colors.ink[900] }} numberOfLines={1}>
            {jaula.nombre}
          </Text>
          <Badge label={prestada ? (vencida ? 'VENCIDA' : 'OCUPADA') : 'LIBRE'} tone={prestada ? (vencida ? 'danger' : 'warning') : 'success'} />
        </View>
        <Text style={{ fontSize: 12, color: colors.ink[500] }} numberOfLines={1}>
          {[jaula.color, jaula.tamano].filter(Boolean).join(' · ') || 'Sin datos'}
        </Text>
        {prestada ? (
          <View style={{ gap: 1, marginTop: 2 }}>
            <Text style={{ fontSize: 13, fontWeight: typography.weights.semibold, color: colors.ink[800] }} numberOfLines={1}>
              {jaula.clienteNombre}
            </Text>
            <Text style={{ fontSize: 12, color: vencida ? colors.danger[600] : colors.ink[500], fontWeight: vencida ? typography.weights.bold : typography.weights.medium }} numberOfLines={1}>
              {vencida ? 'Venció el' : 'Hasta'} {fmtISO(fechaISO(jaula.prestadaHasta))}
            </Text>
          </View>
        ) : null}
      </View>
    </Pressable>
  );
}

// ============================================================================
// Detalle: prestar (si libre) o ver préstamo + devolver (si prestada)
// ============================================================================
function DetalleJaulaSheet({
  jaula,
  canManage,
  perfilNombre,
  onClose,
}: {
  jaula: Jaula | null;
  canManage: boolean;
  perfilNombre: string;
  onClose: () => void;
}) {
  const [nombre, setNombre] = useState('');
  const [celular, setCelular] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [conGarantia, setConGarantia] = useState(false);
  const [garantia, setGarantia] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!jaula) return;
    setNombre('');
    setCelular('');
    setDesde(hoyISO());
    setHasta('');
    setConGarantia(false);
    setGarantia('');
    setError(null);
    setSaving(false);
  }, [jaula]);

  if (!jaula) return null;
  const prestada = jaula.estado === 'prestada';
  const vencida = estaVencida(jaula);
  const hastaIso = fechaISO(jaula.prestadaHasta);
  const dias = diasRestantes(hastaIso);

  const onPrestar = async () => {
    if (!nombre.trim()) return setError('Poné nombre y apellido del cliente.');
    if (!hasta) return setError('Elegí hasta cuándo se presta.');
    if (conGarantia && !garantia.trim()) return setError('Poné el monto de la garantía o elegí "Sin garantía".');
    setSaving(true);
    setError(null);
    try {
      await prestarJaula({
        jaulaId: jaula.id,
        clienteNombre: nombre.trim(),
        clienteCelular: celular.trim() || null,
        garantiaMonto: conGarantia && garantia.trim() ? Number(garantia.replace(',', '.')) : null,
        desde: desde || null,
        hasta,
        prestadaPorNombre: perfilNombre,
      });
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo prestar.');
    } finally {
      setSaving(false);
    }
  };

  const onDevolver = async () => {
    setSaving(true);
    setError(null);
    try {
      await devolverJaula(jaula.id);
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo registrar la devolución.');
    } finally {
      setSaving(false);
    }
  };

  const onWhatsapp = () => {
    if (!jaula.clienteCelular) return;
    const msg = `Hola${jaula.clienteNombre ? ` ${jaula.clienteNombre}` : ''}, te escribimos de CliniCAT. Te recordamos la devolución de la jaula prestada${hastaIso ? ` (vencía el ${fmtISO(hastaIso)})` : ''}. ¡Gracias!`;
    Linking.openURL(whatsappLink(jaula.clienteCelular, msg));
  };

  return (
    <Sheet
      visible={!!jaula}
      onClose={onClose}
      size="md"
      title={jaula.nombre}
      subtitle={[jaula.color, jaula.tamano].filter(Boolean).join(' · ') || undefined}
      footer={
        prestada ? (
          <>
            <Button label="Cerrar" variant="ghost" onPress={onClose} disabled={saving} />
            {canManage ? (
              <Button label="Devuelta" variant="success" iconLeft={<PackageCheck size={16} color={colors.white} />} onPress={onDevolver} loading={saving} />
            ) : null}
          </>
        ) : (
          <>
            <Button label="Cancelar" variant="ghost" onPress={onClose} disabled={saving} />
            {canManage ? <Button label="Prestar" onPress={onPrestar} loading={saving} /> : null}
          </>
        )
      }
    >
      {!prestada ? (
        <Text style={{ textAlign: 'center', fontFamily: typography.display.fontFamily, fontSize: 21, fontWeight: typography.weights.extrabold, color: colors.magenta[700], letterSpacing: 0.8 }}>
          PRESTAR {jaula.nombre.toUpperCase()}
        </Text>
      ) : null}

      {jaula.fotoUrl ? (
        <Image
          source={{ uri: jaula.fotoUrl }}
          style={{ width: '100%', height: 220, borderRadius: radius.xl, backgroundColor: colors.crema[50], /* @ts-ignore */ filter: prestada ? 'grayscale(100%)' : 'none' }}
          // @ts-ignore web (foto completa, sin recortar)
          resizeMode="contain"
        />
      ) : null}

      {prestada ? (
        // ---- Préstamo activo ----
        <View style={{ gap: 10 }}>
          {vencida ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, padding: 10, borderRadius: 10, backgroundColor: colors.danger[50] }}>
              <Clock size={16} color={colors.danger[600]} />
              <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.bold }}>
                Vencida {dias !== null ? `hace ${Math.abs(dias)} día${Math.abs(dias) === 1 ? '' : 's'}` : ''}
              </Text>
            </View>
          ) : dias !== null ? (
            <Text style={{ fontSize: 13, color: colors.ink[600] }}>Faltan {dias} día{dias === 1 ? '' : 's'} para la devolución.</Text>
          ) : null}

          {jaula.descripcion ? <DescripcionJaula texto={jaula.descripcion} /> : null}

          <DatoFila label="Cliente" valor={jaula.clienteNombre ?? '—'} />
          <DatoFila label="Celular" valor={jaula.clienteCelular ?? '—'} />
          <DatoFila label="Garantía" valor={jaula.garantiaMonto != null ? `${jaula.garantiaMonto} Bs` : 'Sin garantía'} />
          <DatoFila label="Prestada el" valor={formatDateTime(jaula.prestadaEn?.toDate?.() ?? null)} />
          <DatoFila label="Desde → Hasta" valor={`${fmtISO(fechaISO(jaula.prestadaDesde))} → ${fmtISO(hastaIso)}`} />
          {jaula.prestadaPorNombre ? <DatoFila label="Prestó" valor={jaula.prestadaPorNombre} /> : null}

          {jaula.clienteCelular ? (
            <Button
              label="Escribir por WhatsApp"
              variant="success"
              iconLeft={<MessageCircle size={16} color={colors.white} />}
              onPress={onWhatsapp}
            />
          ) : null}
        </View>
      ) : (
        // ---- Formulario de préstamo ----
        canManage ? (
          <View style={{ gap: 12 }}>
            {jaula.descripcion ? <DescripcionJaula texto={jaula.descripcion} /> : null}
            <Text style={{ fontSize: 13, color: colors.ink[600] }}>Completá los datos para prestar esta jaula.</Text>
            <TextField label="Nombre y apellido" placeholder="Ej: María Pérez" value={nombre} onChangeText={setNombre} autoCapitalize="words" />
            <TextField label="Celular" placeholder="Ej: 70012345" value={celular} onChangeText={setCelular} keyboardType="phone-pad" />
            <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
              <FechaInput label="Desde" value={desde} onChange={setDesde} />
              <FechaInput label="Hasta (devolución)" value={hasta} onChange={setHasta} />
            </View>

            {/* Garantía */}
            <View style={{ gap: 8 }}>
              <CampoLabel>Garantía</CampoLabel>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <ChipGarantia label="Sin garantía" activo={!conGarantia} onPress={() => { setConGarantia(false); setGarantia(''); }} />
                <ChipGarantia label="Con garantía" activo={conGarantia} onPress={() => setConGarantia(true)} />
              </View>
              {conGarantia ? (
                <TextField
                  label="Monto de la garantía (Bs)"
                  placeholder="Ej: 100"
                  value={garantia}
                  onChangeText={setGarantia}
                  keyboardType="numeric"
                  iconLeft={<Text style={{ fontSize: 13, fontWeight: typography.weights.bold, color: colors.ink[500] }}>Bs</Text>}
                />
              ) : null}
            </View>
          </View>
        ) : (
          <Text style={{ fontSize: 13, color: colors.ink[500] }}>Esta jaula está disponible. Recepción o administración puede prestarla.</Text>
        )
      )}

      {error ? (
        <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
          <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>{error}</Text>
        </View>
      ) : null}
    </Sheet>
  );
}

function DatoFila({ label, valor }: { label: string; valor: string }) {
  return (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 12, paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: colors.ink[100] }}>
      <Text style={{ fontSize: 13, color: colors.ink[500] }}>{label}</Text>
      <Text style={{ fontSize: 13, fontWeight: typography.weights.semibold, color: colors.ink[900], textAlign: 'right', flex: 1 }}>{valor}</Text>
    </View>
  );
}

// Caja resaltada con el estado/descripción de la jaula (lo que el cliente debe saber)
function DescripcionJaula({ texto }: { texto: string }) {
  return (
    <View style={{ flexDirection: 'row', gap: 9, padding: 12, borderRadius: 12, backgroundColor: colors.warning[50], borderWidth: 1, borderColor: colors.warning[500] }}>
      <AlertTriangle size={17} color={colors.warning[600]} style={{ marginTop: 1 }} />
      <View style={{ flex: 1 }}>
        <Text style={{ fontSize: 11, fontWeight: typography.weights.bold, color: colors.warning[600], letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 2 }}>
          Estado de la jaula
        </Text>
        <Text style={{ fontSize: 13.5, color: colors.ink[800], lineHeight: 19 }}>{texto}</Text>
      </View>
    </View>
  );
}

function ChipGarantia({ label, activo, onPress }: { label: string; activo: boolean; onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      style={{
        flex: 1,
        alignItems: 'center',
        paddingVertical: 10,
        borderRadius: 12,
        borderWidth: 1.5,
        borderColor: activo ? colors.magenta[600] : colors.ink[200],
        backgroundColor: activo ? colors.magenta[600] : colors.white,
        // @ts-ignore web only
        cursor: 'pointer',
      }}
    >
      <Text style={{ fontSize: 13, fontWeight: typography.weights.semibold, color: activo ? colors.white : colors.ink[700] }}>{label}</Text>
    </Pressable>
  );
}

// ============================================================================
// Editar / crear jaula (foto, color, tamaño, nombre)
// ============================================================================
function EditarJaulaSheet({ jaula, visible, onClose }: { jaula: Jaula | null; visible: boolean; onClose: () => void }) {
  const [nombre, setNombre] = useState('');
  const [color, setColor] = useState('');
  const [tamano, setTamano] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [fotoUrl, setFotoUrl] = useState<string | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    setNombre(jaula?.nombre ?? '');
    setColor(jaula?.color ?? '');
    setTamano(jaula?.tamano ?? '');
    setDescripcion(jaula?.descripcion ?? '');
    setFotoUrl(jaula?.fotoUrl ?? null);
    setError(null);
    setSaving(false);
    setSubiendo(false);
    setConfirmarBorrar(false);
  }, [visible, jaula]);

  const prestada = jaula?.estado === 'prestada';

  const onEliminar = async () => {
    if (!jaula) return;
    setSaving(true);
    setError(null);
    try {
      await eliminarJaula(jaula.id);
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo eliminar la jaula.');
      setSaving(false);
    }
  };

  const onPickFoto = () =>
    elegirFoto(async (file) => {
      setSubiendo(true);
      setError(null);
      try {
        const r = await subirACloudinary(file, { folder: 'clinicat/jaulas' });
        setFotoUrl(r.url);
      } catch (e: any) {
        setError(e?.message ?? 'No se pudo subir la foto.');
      } finally {
        setSubiendo(false);
      }
    }, setError);

  const onSave = async () => {
    if (!nombre.trim()) return setError('Poné un nombre (ej: Jaula 1).');
    setSaving(true);
    setError(null);
    try {
      await upsertJaula({ id: jaula?.id, nombre: nombre.trim(), color: color.trim() || null, tamano: tamano.trim() || null, descripcion: descripcion.trim() || null, fotoUrl });
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      size="sm"
      title={jaula ? `Editar ${jaula.nombre}` : 'Nueva jaula'}
      subtitle="Foto, color y tamaño"
      footer={
        <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          {/* Eliminar: al extremo izquierdo (solo al editar una jaula libre) */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            {jaula && !prestada ? (
              confirmarBorrar ? (
                <>
                  <Text style={{ fontSize: 12, color: colors.danger[600] }}>¿Eliminar?</Text>
                  <Pressable onPress={() => setConfirmarBorrar(false)} disabled={saving}>
                    <Text style={{ fontSize: 12, color: colors.ink[500], fontWeight: typography.weights.semibold }}>No</Text>
                  </Pressable>
                  <Pressable onPress={onEliminar} disabled={saving}>
                    <Text style={{ fontSize: 12, color: colors.danger[600], fontWeight: typography.weights.bold }}>{saving ? 'Eliminando…' : 'Sí'}</Text>
                  </Pressable>
                </>
              ) : (
                <Pressable onPress={() => setConfirmarBorrar(true)} style={{ flexDirection: 'row', alignItems: 'center', gap: 5, paddingVertical: 4 }}>
                  <Trash2 size={14} color={colors.ink[400]} />
                  <Text style={{ fontSize: 12, color: colors.ink[400] }}>Eliminar</Text>
                </Pressable>
              )
            ) : null}
          </View>

          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Button label="Cancelar" variant="ghost" onPress={onClose} disabled={saving} />
            <Button label={jaula ? 'Guardar' : 'Crear'} onPress={onSave} loading={saving} disabled={subiendo} />
          </View>
        </View>
      }
    >
      <Pressable
        onPress={onPickFoto}
        style={({ hovered }) => ({
          height: 170,
          borderRadius: radius.xl,
          borderWidth: 1.5,
          borderStyle: 'dashed',
          borderColor: colors.magenta[300],
          backgroundColor: hovered ? colors.magenta[50] : colors.crema[50],
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          // @ts-ignore web only
          cursor: 'pointer',
        })}
      >
        {fotoUrl ? (
          <Image source={{ uri: fotoUrl }} style={{ width: '100%', height: '100%' }} /* @ts-ignore */ resizeMode="cover" />
        ) : (
          <View style={{ alignItems: 'center', gap: 6 }}>
            <Camera size={26} color={colors.magenta[600]} />
            <Text style={{ fontSize: 13, color: colors.ink[600], fontWeight: typography.weights.semibold }}>{subiendo ? 'Subiendo…' : 'Tocá para subir foto'}</Text>
          </View>
        )}
      </Pressable>
      {fotoUrl ? (
        <Pressable onPress={onPickFoto}>
          <Text style={{ fontSize: 12, color: colors.magenta[700], fontWeight: typography.weights.bold, textAlign: 'center' }}>Cambiar foto</Text>
        </Pressable>
      ) : null}

      <TextField label="Nombre" placeholder="Jaula 1" value={nombre} onChangeText={setNombre} />
      <SelectorTamano value={tamano} onChange={setTamano} />
      <SelectorColor value={color} onChange={setColor} />
      <View style={{ gap: 6 }}>
        <Text style={{ fontSize: 13, fontWeight: typography.weights.semibold, color: colors.ink[700] }}>Descripción / estado</Text>
        <TextInput
          value={descripcion}
          onChangeText={setDescripcion}
          placeholder="Ej: le falta un tornillo en la traba…"
          placeholderTextColor={colors.ink[400]}
          multiline
          textAlignVertical="top"
          style={{
            minHeight: 84,
            borderWidth: 1.5,
            borderColor: colors.ink[200],
            borderRadius: radius.xl,
            paddingHorizontal: 14,
            paddingVertical: 11,
            fontSize: typography.sizes.md,
            color: colors.ink[900],
            fontFamily: typography.body.fontFamily,
            backgroundColor: colors.white,
            // @ts-ignore web only
            outlineStyle: 'none',
          }}
        />
        <Text style={{ fontSize: 12, color: colors.ink[500] }}>
          Lo que el cliente debe saber antes de llevarse la jaula (ej: le falta un tornillo, una traba floja…)
        </Text>
      </View>

      {error ? (
        <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
          <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>{error}</Text>
        </View>
      ) : null}
    </Sheet>
  );
}

// ---- Selectores de tamaño y color para la jaula ----
const TAMANOS = ['Pequeña', 'Mediana', 'Grande'];
const COLORES: { nombre: string; hex: string }[] = [
  { nombre: 'Blanco', hex: '#FFFFFF' },
  { nombre: 'Negro', hex: '#1F2937' },
  { nombre: 'Gris', hex: '#9CA3AF' },
  { nombre: 'Plomo', hex: '#6B7280' },
  { nombre: 'Rojo', hex: '#DC2626' },
  { nombre: 'Fucsia', hex: '#D6249F' },
  { nombre: 'Rosado', hex: '#F472B6' },
  { nombre: 'Naranja', hex: '#F97316' },
  { nombre: 'Amarillo', hex: '#FACC15' },
  { nombre: 'Verde', hex: '#16A34A' },
  { nombre: 'Azul', hex: '#2563EB' },
  { nombre: 'Celeste', hex: '#38BDF8' },
  { nombre: 'Morado', hex: '#7C3AED' },
  { nombre: 'Café', hex: '#92400E' },
  { nombre: 'Beige', hex: '#E7D8C9' },
];

function CampoLabel({ children }: { children: string }) {
  return (
    <Text style={{ fontSize: 11, fontWeight: typography.weights.bold, color: colors.ink[500], letterSpacing: 0.6, textTransform: 'uppercase' }}>
      {children}
    </Text>
  );
}

function SelectorTamano({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <View style={{ gap: 6 }}>
      <CampoLabel>Tamaño</CampoLabel>
      <View style={{ flexDirection: 'row', gap: 8 }}>
        {TAMANOS.map((t) => {
          const sel = value === t;
          return (
            <Pressable
              key={t}
              onPress={() => onChange(sel ? '' : t)}
              style={{
                flex: 1,
                alignItems: 'center',
                paddingVertical: 10,
                borderRadius: 12,
                borderWidth: 1.5,
                borderColor: sel ? colors.magenta[600] : colors.ink[200],
                backgroundColor: sel ? colors.magenta[600] : colors.white,
                // @ts-ignore web only
                cursor: 'pointer',
              }}
            >
              <Text style={{ fontSize: 13, fontWeight: typography.weights.semibold, color: sel ? colors.white : colors.ink[700] }}>{t}</Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

function SelectorColor({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [q, setQ] = useState('');
  const filtro = q.trim().toLowerCase();
  const sel = COLORES.find((c) => c.nombre === value);
  const lista = filtro ? COLORES.filter((c) => c.nombre.toLowerCase().includes(filtro)) : [];

  return (
    <View style={{ gap: 6 }}>
      <CampoLabel>Color</CampoLabel>

      {/* Color elegido */}
      {value ? (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, alignSelf: 'flex-start', paddingHorizontal: 11, paddingVertical: 7, borderRadius: 999, borderWidth: 1.5, borderColor: colors.magenta[300], backgroundColor: colors.magenta[50] }}>
          <View style={{ width: 16, height: 16, borderRadius: 999, backgroundColor: sel?.hex ?? colors.ink[300], borderWidth: 1, borderColor: 'rgba(0,0,0,0.15)' }} />
          <Text style={{ fontSize: 13, fontWeight: typography.weights.bold, color: colors.ink[800] }}>{value}</Text>
          <Pressable onPress={() => onChange('')} hitSlop={6}>
            <Text style={{ fontSize: 13, color: colors.ink[400], fontWeight: typography.weights.bold }}>✕</Text>
          </Pressable>
        </View>
      ) : null}

      <TextField placeholder="Escribí un color… (ej: fucsia)" value={q} onChangeText={setQ} />

      {/* Coincidencias (solo al escribir) */}
      {filtro ? (
        <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
          {lista.map((c) => (
            <Pressable
              key={c.nombre}
              onPress={() => { onChange(c.nombre); setQ(''); }}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 7,
                paddingHorizontal: 11,
                paddingVertical: 8,
                borderRadius: 999,
                borderWidth: 1.5,
                borderColor: colors.ink[200],
                backgroundColor: colors.white,
                // @ts-ignore web only
                cursor: 'pointer',
              }}
            >
              <View style={{ width: 16, height: 16, borderRadius: 999, backgroundColor: c.hex, borderWidth: 1, borderColor: 'rgba(0,0,0,0.15)' }} />
              <Text style={{ fontSize: 13, fontWeight: typography.weights.medium, color: colors.ink[800] }}>{c.nombre}</Text>
            </Pressable>
          ))}
          {lista.length === 0 ? <Text style={{ fontSize: 13, color: colors.ink[400] }}>Sin coincidencias</Text> : null}
        </View>
      ) : null}
    </View>
  );
}

// ============================================================================
// Historial de préstamos (sección inline debajo de las jaulas) + export
// ============================================================================
const HIST_COLS: { key: string; label: string; flex: number }[] = [
  { key: 'jaula', label: 'Jaula', flex: 0.9 },
  { key: 'cliente', label: 'Cliente', flex: 1.4 },
  { key: 'celular', label: 'Celular', flex: 1 },
  { key: 'garantia', label: 'Garantía', flex: 0.9 },
  { key: 'desde', label: 'Desde', flex: 0.9 },
  { key: 'hasta', label: 'Hasta', flex: 0.9 },
  { key: 'prestada', label: 'Prestada el', flex: 1.3 },
  { key: 'presto', label: 'Prestó', flex: 1 },
  { key: 'estado', label: 'Estado', flex: 0.9 },
  { key: 'devuelta', label: 'Devuelta el', flex: 1.3 },
];

const garantiaTxt = (m?: number | null): string => (m != null ? `${m} Bs` : '—');

function HistorialPrestamos({ prestamos, wide }: { prestamos: PrestamoJaula[]; wide: boolean }) {
  const filas: FilaPrestamoJaula[] = prestamos.map((p) => ({
    jaula: p.jaulaNombre,
    cliente: p.clienteNombre,
    celular: p.clienteCelular ?? '',
    garantia: garantiaTxt(p.garantiaMonto),
    desde: fmtISO(fechaISO(p.desde)),
    hasta: fmtISO(fechaISO(p.hasta)),
    prestadaEl: formatDateTime(p.prestadaEn?.toDate?.() ?? null),
    presto: p.prestadaPorNombre ?? '',
    estado: p.devueltaEn ? `Devuelta el ${formatDateTime(p.devueltaEn.toDate())}` : 'Prestada',
  }));

  return (
    <Card padding={0} style={{ overflow: 'hidden' }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', paddingHorizontal: 16, paddingVertical: 12, backgroundColor: colors.crema[50], borderBottomWidth: 1, borderBottomColor: colors.ink[100] }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <History size={18} color={colors.magenta[700]} />
          <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 16, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
            Historial de préstamos
          </Text>
        </View>
        {prestamos.length > 0 ? (
          <View style={{ flexDirection: 'row', gap: 8, marginLeft: 'auto' }}>
            <Button label="Excel" variant="success" size="sm" iconLeft={<Download size={14} color={colors.white} />} onPress={() => exportPrestamosJaulaExcel(filas).catch((e: any) => alert(e?.message ?? 'No se pudo exportar.'))} />
            <Button label="PDF" variant="danger" size="sm" iconLeft={<FileText size={14} color={colors.white} />} onPress={() => exportPrestamosJaulaPDF(filas).catch((e: any) => alert(e?.message ?? 'No se pudo exportar.'))} />
          </View>
        ) : null}
      </View>

      {prestamos.length === 0 ? (
        <View style={{ padding: 16 }}>
          <Text style={{ fontSize: 13, color: colors.ink[500], textAlign: 'center' }}>
            Todavía no hay préstamos. Acá vas a ver cada uno: qué jaula, a quién, cuándo y por cuánto tiempo.
          </Text>
        </View>
      ) : wide ? (
        // ---- PC: tabla por columnas ----
        <View>
          {/* Encabezado de columnas */}
          <View style={{ flexDirection: 'row', gap: 10, paddingHorizontal: 16, paddingVertical: 9, borderBottomWidth: 1, borderBottomColor: colors.ink[100] }}>
            {HIST_COLS.map((col) => (
              <Text key={col.key} style={{ flex: col.flex, fontSize: 11, fontWeight: typography.weights.bold, color: colors.ink[400], letterSpacing: 0.5, textTransform: 'uppercase' }} numberOfLines={1}>
                {col.label}
              </Text>
            ))}
          </View>
          {prestamos.map((p, idx) => {
            const activo = !p.devueltaEn;
            return (
              <View
                key={p.id}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 10,
                  paddingHorizontal: 16,
                  paddingVertical: 11,
                  backgroundColor: idx % 2 === 1 ? 'rgba(250,247,244,0.5)' : colors.white,
                  borderBottomWidth: idx === prestamos.length - 1 ? 0 : 1,
                  borderBottomColor: colors.ink[100],
                }}
              >
                <Text style={{ flex: HIST_COLS[0].flex, fontSize: 13, fontWeight: typography.weights.bold, color: colors.ink[900] }} numberOfLines={1}>{p.jaulaNombre}</Text>
                <Text style={{ flex: HIST_COLS[1].flex, fontSize: 13, color: colors.ink[800] }} numberOfLines={1}>{p.clienteNombre}</Text>
                <Text style={{ flex: HIST_COLS[2].flex, fontSize: 13, color: colors.ink[600] }} numberOfLines={1}>{p.clienteCelular || '—'}</Text>
                <Text style={{ flex: HIST_COLS[3].flex, fontSize: 13, color: p.garantiaMonto != null ? colors.ink[800] : colors.ink[300], fontWeight: p.garantiaMonto != null ? typography.weights.semibold : typography.weights.regular }} numberOfLines={1}>{garantiaTxt(p.garantiaMonto)}</Text>
                <Text style={{ flex: HIST_COLS[4].flex, fontSize: 13, color: colors.ink[600] }} numberOfLines={1}>{fmtISO(fechaISO(p.desde))}</Text>
                <Text style={{ flex: HIST_COLS[5].flex, fontSize: 13, color: colors.ink[600] }} numberOfLines={1}>{fmtISO(fechaISO(p.hasta))}</Text>
                <Text style={{ flex: HIST_COLS[6].flex, fontSize: 12, color: colors.ink[600] }} numberOfLines={1}>{formatDateTime(p.prestadaEn?.toDate?.() ?? null)}</Text>
                <Text style={{ flex: HIST_COLS[7].flex, fontSize: 13, color: colors.ink[600] }} numberOfLines={1}>{p.prestadaPorNombre || '—'}</Text>
                <View style={{ flex: HIST_COLS[8].flex, alignItems: 'center' }}>
                  <Badge label={activo ? 'PRESTADA' : 'DEVUELTA'} tone={activo ? 'danger' : 'success'} />
                </View>
                <Text style={{ flex: HIST_COLS[9].flex, fontSize: 12, color: activo ? colors.ink[300] : colors.success[600], fontWeight: activo ? typography.weights.regular : typography.weights.semibold }} numberOfLines={1}>
                  {p.devueltaEn ? formatDateTime(p.devueltaEn.toDate()) : '—'}
                </Text>
              </View>
            );
          })}
        </View>
      ) : (
        // ---- Celu: tarjetas ----
        prestamos.map((p, idx) => {
          const activo = !p.devueltaEn;
          return (
            <View
              key={p.id}
              style={{
                paddingHorizontal: 16,
                paddingVertical: 12,
                gap: 6,
                backgroundColor: idx % 2 === 1 ? 'rgba(250,247,244,0.5)' : colors.white,
                borderBottomWidth: idx === prestamos.length - 1 ? 0 : 1,
                borderBottomColor: colors.ink[100],
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <Text style={{ fontSize: 14, fontWeight: typography.weights.bold, color: colors.ink[900] }} numberOfLines={1}>
                  {p.jaulaNombre} → {p.clienteNombre}
                </Text>
                <Badge label={activo ? 'PRESTADA' : 'DEVUELTA'} tone={activo ? 'danger' : 'success'} />
              </View>
              <HistDato label="Celular" valor={p.clienteCelular || '—'} />
              <HistDato label="Garantía" valor={garantiaTxt(p.garantiaMonto)} />
              <HistDato label="Desde → Hasta" valor={`${fmtISO(fechaISO(p.desde))} → ${fmtISO(fechaISO(p.hasta))}`} />
              <HistDato label="Prestada el" valor={formatDateTime(p.prestadaEn?.toDate?.() ?? null)} />
              {p.prestadaPorNombre ? <HistDato label="Prestó" valor={p.prestadaPorNombre} /> : null}
              {p.devueltaEn ? <HistDato label="Devuelta el" valor={formatDateTime(p.devueltaEn.toDate())} /> : null}
            </View>
          );
        })
      )}
    </Card>
  );
}

function HistDato({ label, valor }: { label: string; valor: string }) {
  return (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 12 }}>
      <Text style={{ fontSize: 12, color: colors.ink[400] }}>{label}</Text>
      <Text style={{ fontSize: 12, color: colors.ink[700], fontWeight: typography.weights.medium, textAlign: 'right', flex: 1 }} numberOfLines={1}>{valor}</Text>
    </View>
  );
}

function FechaInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  if (Platform.OS !== 'web') {
    return <TextField containerStyle={{ flex: 1, minWidth: 130 }} label={label} value={value} onChangeText={onChange} placeholder="YYYY-MM-DD" />;
  }
  return (
    <View style={{ gap: 4, flex: 1, minWidth: 130 }}>
      <Text style={{ fontSize: 11, fontWeight: typography.weights.bold, color: colors.ink[500], letterSpacing: 0.6, textTransform: 'uppercase' }}>{label}</Text>
      {/* @ts-ignore web-only native input */}
      <input
        type="date"
        value={value}
        onChange={(e: any) => onChange(e.target.value)}
        style={{
          height: 46,
          padding: '0 12px',
          borderRadius: 12,
          border: `1.5px solid ${colors.ink[200]}`,
          background: colors.white,
          color: colors.ink[900],
          fontSize: 14,
          fontFamily: 'inherit',
          outline: 'none',
          width: '100%',
          boxSizing: 'border-box',
        }}
      />
    </View>
  );
}
